// Phase 28 — God-mode admin user editing.
//
// Renders the editable profile form dynamically from
// `GET /api/admin/users/{id}/profile-schema` so the UI doesn't hardcode
// any field names — when the backend adds a new editable field, this tab
// picks it up automatically.

import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, Save, RotateCcw } from "lucide-react";
import { toast } from "sonner";

function isEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a == null || b == null) return false;
  if (typeof a === "object") return JSON.stringify(a) === JSON.stringify(b);
  return false;
}

export default function EditProfileTab({ userId, onPatched }) {
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [originals, setOriginals] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    api.get(`/admin/users/${userId}/profile-schema`)
      .then(({ data }) => {
        setSchema(data);
        const seed = {};
        for (const f of data.fields || []) seed[f.name] = f.current;
        setValues(seed);
        setOriginals({ ...seed });
      })
      .catch((e) => setError(e?.response?.data?.detail || e.message || "Failed to load schema"))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const dirtyKeys = Object.keys(values).filter((k) => !isEqual(values[k], originals[k]));
  const isDirty = dirtyKeys.length > 0;

  const handleSave = async () => {
    if (!isDirty) return;
    setSaving(true);
    try {
      const patch = {};
      for (const k of dirtyKeys) patch[k] = values[k];
      const { data } = await api.patch(`/admin/users/${userId}/profile`, patch);
      toast.success(`Saved ${dirtyKeys.length} field${dirtyKeys.length === 1 ? "" : "s"}`);
      // Refresh from server so token_version etc. are correct.
      load();
      onPatched?.(data.user);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.message || e.message || "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = () => setValues({ ...originals });

  if (loading) {
    return (
      <div className="flex items-center gap-2 pt-4 text-sm text-slate-500" data-testid="edit-tab-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading schema…
      </div>
    );
  }
  if (error || !schema) {
    return <div className="pt-4 text-sm text-red-600" data-testid="edit-tab-error">{String(error || "No schema")}</div>;
  }

  return (
    <div className="space-y-4 pt-2" data-testid="edit-profile-tab">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Editable fields</div>
          {schema.updated_at && (
            <span className="text-[10px] text-slate-400" data-testid="edit-tab-updated-at">
              v{schema.version} · updated {new Date(schema.updated_at).toLocaleString()}
            </span>
          )}
        </div>

        <div className="grid grid-cols-1 gap-3">
          {schema.fields.map((f) => (
            <FieldRow
              key={f.name}
              field={f}
              value={values[f.name]}
              onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))}
            />
          ))}
        </div>
      </div>

      <div className="sticky bottom-0 -mx-2 flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white/95 px-4 py-3 backdrop-blur">
        <div className="text-xs text-slate-500" data-testid="edit-tab-dirty-count">
          {isDirty ? (
            <span className="font-semibold text-amber-700">{dirtyKeys.length} unsaved change{dirtyKeys.length === 1 ? "" : "s"}</span>
          ) : (
            <span>No changes</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={handleRevert} disabled={!isDirty || saving} data-testid="edit-tab-revert">
            <RotateCcw className="me-1 h-3.5 w-3.5" /> Revert
          </Button>
          <Button size="sm" onClick={handleSave} disabled={!isDirty || saving} data-testid="edit-tab-save">
            {saving ? <Loader2 className="me-1 h-3.5 w-3.5 animate-spin" /> : <Save className="me-1 h-3.5 w-3.5" />}
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function FieldRow({ field, value, onChange }) {
  const label = field.label || field.name;
  const testid = `edit-field-${field.name}`;

  // Booleans → Switch
  if (field.type === "bool") {
    return (
      <div className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2" data-testid={testid}>
        <Label className="text-sm font-medium text-slate-800">{label}</Label>
        <Switch
          checked={Boolean(value)}
          onCheckedChange={(v) => onChange(v)}
          data-testid={`${testid}-switch`}
        />
      </div>
    );
  }

  // Currency / enums with options → Select
  if (field.options && Array.isArray(field.options)) {
    return (
      <div className="grid gap-1.5" data-testid={testid}>
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
        <Select value={value || ""} onValueChange={(v) => onChange(v)}>
          <SelectTrigger className="h-9 text-sm" data-testid={`${testid}-select`}>
            <SelectValue placeholder="—" />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((o) => (
              <SelectItem key={o} value={o}>{o}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  // notification_preferences object → grid of switches
  if (field.type === "object" && field.shape) {
    const obj = value || {};
    return (
      <div className="grid gap-2 rounded-lg border border-slate-100 p-3" data-testid={testid}>
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {Object.keys(field.shape).map((k) => (
            <div key={k} className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-1.5">
              <span className="text-sm capitalize text-slate-700">{k.replace(/_/g, " ")}</span>
              <Switch
                checked={Boolean(obj[k])}
                onCheckedChange={(v) => onChange({ ...obj, [k]: v })}
                data-testid={`${testid}-${k}-switch`}
              />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Default: text / email / url / string → Input
  const inputType = field.type === "email" ? "email" : field.type === "url" ? "url" : "text";
  return (
    <div className="grid gap-1.5" data-testid={testid}>
      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
      <Input
        type={inputType}
        value={value ?? ""}
        maxLength={field.max || undefined}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.optional ? "(optional)" : ""}
        data-testid={`${testid}-input`}
        className="h-9 text-sm"
      />
    </div>
  );
}
